import type { FC } from 'react';
import nl2br from 'react-nl2br';
import { Likes } from '~/app/_components/likes';
import { UsernameWithBadges } from '~/app/_components/username-with-badge/username-with-badge';
import noThumbnailWrapperImage from '~/common/assets/images/story-series-no-thumbnail.png';
import type { SeriesOverview } from '~/common/types/series-overview';
import { resizeServingUrl } from '~/utils/image';
import { routes } from '~/utils/routes';
import { isNoIndexSeries } from '~/utils/series';
import { Link } from '@app/_components/link';
import { css } from '@styled-system/css';
import { styled } from '@styled-system/jsx';
import {
  Box,
  Center,
  HStack,
  Paragraph,
  VStack,
  VstackProps,
} from '@styled-system/jsx';
import forFollowerIcon from './assets/for_followers_icon.png';
import forMutualFollowIcon from './assets/for_mutual_follow_icon.png';
import { Badge } from './badge';
import { NovelBadge } from './novel-badge';

export type Visibilities = Partial<{
  user: boolean;
  likes: boolean;
  publicationDate: boolean;
  description: boolean;
  tags: boolean;
  noBackground: boolean;
  pcBottomBorder: boolean;
  descriptionLineClamp: number;
}>;

export type SeriesListItemConfigs = Partial<{
  visibilities: Visibilities;
  linkToStory?: {
    id?: string;
  };
}>;

export const DefaultConfigs: SeriesListItemConfigs = {
  visibilities: {
    user: true,
    likes: true,
    publicationDate: false,
    description: true,
    tags: true,
    noBackground: false,
    pcBottomBorder: false,
    descriptionLineClamp: 2,
  },
};

type AnalyticsParams = {
  sectionTitle: string;
};

type Props = {
  index: number;
  series: SeriesOverview;
  config?: SeriesListItemConfigs;
  showSensitive: boolean;
  analyticsParams?: AnalyticsParams;
  // default is enabled
  useNextLink?: boolean;
} & VstackProps;

export const SeriesListItem: FC<Props> = ({
  index,
  series,
  config,
  showSensitive,
  analyticsParams,
  useNextLink,
  ...vstackProps
}) => {
  const isLinkedToStory = !!(config?.linkToStory && config.linkToStory?.id);

  const link =
    config?.linkToStory && config.linkToStory?.id
      ? routes.story.url(config.linkToStory.id)
      : routes.series.url(series.id);

  // SEO対策: noindex指定されているシリーズの場合、rel="nofollow"を付与する
  const linkRel = isLinkedToStory
    ? undefined
    : isNoIndexSeries(series)
    ? 'nofollow'
    : undefined;

  const configs = {
    ...DefaultConfigs,
    ...config,
  };

  return (
    <VStack
      cursor="pointer"
      color="accent.700"
      background={
        configs.visibilities?.noBackground ? 'none' : 'surface_primary'
      }
      position="relative"
      w="100%"
      px="16px"
      _hover={{
        bg: configs.visibilities?.noBackground ? 'none' : 'highlight',
      }}
      gap="4px"
      py="12px"
      alignItems="flex-start"
      borderBottom={{
        base: '1px solid token(colors.separator)',
        lg: configs.visibilities?.pcBottomBorder
          ? '1px solid token(colors.accent.50)'
          : 'none',
      }}
      _lastOfType={{
        borderBottom: 'none',
      }}
      data-gtm-event-imp="feed_item_imp"
      data-gtm-event-click="feed_item_tapped"
      data-gtm-param-series-id={series.id}
      data-gtm-param-state={index}
      data-gtm-param-recommender-model-id={series.recommenderModelId}
      data-gtm-param-section-title={analyticsParams?.sectionTitle}
      className={css({
        '& a:hover': {
          opacity: '0.8',
        },
      })}
      {...vstackProps}
    >
      {/* Top Container */}
      <HStack w="100%" alignItems="flex-start" zIndex={0}>
        <Link href={link} useNextLink={useNextLink} rel={linkRel}>
          <Box
            w="132px"
            h="88px"
            overflow="hidden"
            borderRadius="3px"
            position="relative"
          >
            <img
              src={
                series.thumbnailURL
                  ? resizeServingUrl(
                      series.thumbnailURL,
                      350,
                      false,
                      !series.hasSensitiveStories || showSensitive
                        ? undefined
                        : 'default'
                    ) ?? noThumbnailWrapperImage
                  : noThumbnailWrapperImage
              }
              alt={series.title}
              loading="lazy"
              className={css({
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '3px',
              })}
            />
            {/* Novel Badge */}
            {series.isNovel && <NovelBadge />}
            {/* Senstive Badge or Complete Badge  */}
            {series.hasSensitiveStories ? (
              <Badge label="センシティブ" width={'auto'} />
            ) : (
              <>
                {series.isOneshot ? (
                  <Badge label="読切" />
                ) : series.isCompleted ? (
                  <Badge label="完結" />
                ) : (
                  <></>
                )}
              </>
            )}

            {/* Publish For Badge */}
            {(series.availability === 'ONLY_FOLLOWERS' ||
              series.availability === 'ONLY_MUTUAL_FOLLOW') && (
              <Center
                position="absolute"
                bottom="0"
                left="0"
                bg="rgba(102, 102, 102, 0.6)"
                backdropFilter="blur(1px)"
                borderRadius="0px 4px 0px 0px"
                w="20px"
                h="20px"
              >
                <img
                  alt="公開範囲設定"
                  className={css({
                    w: '16px',
                  })}
                  width="16px"
                  height="16px"
                  src={
                    series.availability === 'ONLY_FOLLOWERS'
                      ? forFollowerIcon
                      : forMutualFollowIcon
                  }
                />
              </Center>
            )}
          </Box>
        </Link>
        <VStack
          w="100%"
          minHeight="88px"
          justifyContent="flex-start"
          alignItems="flex-start"
          pr="16px"
          gap="0"
        >
          <Link
            href={link}
            className={css({
              w: '100%',
              h: '100%',
            })}
            useNextLink={useNextLink}
            rel={linkRel}
          >
            <VStack w="100%" h="100%" flex="1" gap="0">
              <Paragraph
                fontSize="size_14"
                fontWeight="bold"
                color="accent.1000"
                maxLines="2"
                lineClamp="2"
                w="100%"
                lineBreak="anywhere"
              >
                {series.title}
              </Paragraph>
              {/* Description */}
              {configs.visibilities?.description &&
                series.description.length > 0 && (
                  <Paragraph
                    maxHeight="50px"
                    marginTop="8px"
                    fontSize="size_12"
                    color="content_secondary"
                    lineClamp={
                      configs.visibilities?.descriptionLineClamp || '2'
                    }
                    maxLines={configs.visibilities?.descriptionLineClamp || '2'}
                    pointerEvents="none"
                    mb="10px"
                    data-nosnippet
                    zIndex={0}
                    textAlign="left"
                    marginRight="auto"
                    lineBreak="anywhere"
                  >
                    {nl2br(series.description)}
                  </Paragraph>
                )}
            </VStack>
          </Link>
        </VStack>
      </HStack>

      {/* Tags */}
      {configs.visibilities?.tags && (
        <HStack
          w="100%"
          display="flex"
          flexDir="row"
          marginTop="4px"
          WebkitOverflowScrolling="touch"
          alignItems="center"
          overflowX="auto"
          overflowY="hidden"
          flexWrap="nowrap"
          gap="8px"
          scrollbar={{ base: 'hidden', lg: 'auto' }}
        >
          {series.tags.slice(0, 6).map((tag) => (
            <styled.a
              backgroundColor="strong_bg"
              padding="2px 5px"
              borderRadius="4px"
              key={tag}
              flex="0 0 auto"
              href={`/tag/${encodeURIComponent(tag || '')}`}
            >
              <Paragraph fontSize="size_11" color="tint" maxLines="1">
                #{tag}
              </Paragraph>
            </styled.a>
          ))}
        </HStack>
      )}
      <HStack justifyContent="space-between" w="100%">
        {/* User */}
        {configs.visibilities?.user && series.user && (
          <Link
            href={`/user/${series.user.id}`}
            className={css({ w: '100%', flexGrow: 0 })}
            useNextLink={useNextLink}
          >
            <UsernameWithBadges
              userId={series.user.id}
              username={series.user.name}
              isOfficialWriter={series.user.isOfficialWriter}
              isVip={series.user.isVip}
              disableLink={true}
              className={css({
                w: '100%',
                py: '2px',
              })}
            />
          </Link>
        )}
        {/* Likes */}
        {configs.visibilities?.likes && series.likeCount > 0 && (
          <Link
            href={link}
            className={css({
              // w: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              flexGrow: 1,
              marginRight: '8px',
            })}
            useNextLink={useNextLink}
            rel={linkRel}
          >
            {series.likeCount > 0 ? (
              <Likes likesCount={series.likeCount} variants="normal" />
            ) : (
              <Box w="100%" h="100%" />
            )}
          </Link>
        )}

        {/* Space */}
      </HStack>
    </VStack>
  );
};
